// src/pages/FundPlanner/components/Portfolios/Main/AllocationReport.jsx
import React from "react";

function formatCurrency(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value, digits = 1) {
  if (value == null) return "—";
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

function InlineBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0) * 100));

  return (
    <div className="w-full">
      <div className="h-2 overflow-hidden rounded-full bg-[hsl(var(--muted))]">
        <div
          className="h-full rounded-full bg-[hsl(var(--primary))]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AllocationPanel({ title, rows }) {
  const totalMarketValue = rows.reduce(
    (sum, row) => sum + Number(row.marketValue || 0),
    0
  );
  const totalCount = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-app p-4">
      <div className="text-sm font-semibold text-app">{title}</div>

      <div className="space-y-4">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid items-center gap-3 md:grid-cols-[10rem_minmax(0,1fr)_6rem]"
          >
            <div className="truncate text-sm text-app" title={row.label}>
              {row.label}
            </div>
            <InlineBar value={row.percentOfTotal} />
            <div className="text-right text-sm text-muted">
              {formatPercent(row.percentOfTotal)}
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-[hsl(var(--muted)/0.35)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-app">Category</th>
              <th className="px-4 py-3 font-medium text-app text-right">Market Value</th>
              <th className="px-4 py-3 font-medium text-app text-right">% Total</th>
              <th className="px-4 py-3 font-medium text-app text-right">Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-border">
                <td className="px-4 py-3 text-app">{row.label}</td>
                <td className="px-4 py-3 text-right text-app">
                  {formatCurrency(row.marketValue)}
                </td>
                <td className="px-4 py-3 text-right text-app">
                  {formatPercent(row.percentOfTotal)}
                </td>
                <td className="px-4 py-3 text-right text-app">{row.count}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-border font-semibold">
              <td className="px-4 py-3 text-app">Portfolio Total</td>
              <td className="px-4 py-3 text-right text-app">
                {formatCurrency(totalMarketValue)}
              </td>
              <td className="px-4 py-3 text-right text-app">
                {formatPercent(totalMarketValue ? 1 : 0)}
              </td>
              <td className="px-4 py-3 text-right text-app">{totalCount}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreditRatingSummary({ summary }) {
  const sections = summary?.sections || [];
  const allocation = summary?.combinedAllocation || [];

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-app p-4">
      <div className="text-sm font-semibold text-app">Credit Rating Summary</div>

      <div className="space-y-3">
        {allocation.map((row) => (
          <div
            key={row.label}
            className="grid items-center gap-3 md:grid-cols-[10rem_minmax(0,1fr)_6rem]"
          >
            <div className="truncate text-sm text-app">{row.label}</div>
            <InlineBar value={row.percentOfTotal} />
            <div className="text-right text-sm text-muted">
              {formatPercent(row.percentOfTotal)}
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-[hsl(var(--muted)/0.35)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-app">Rating Distribution</th>
              <th className="px-4 py-3 text-right font-medium text-app">Market Value</th>
              <th className="px-4 py-3 text-right font-medium text-app">Portfolio Allocation</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <React.Fragment key={section.key}>
                <tr className="border-t border-border bg-[hsl(var(--muted)/0.25)]">
                  <td colSpan={3} className="px-4 py-2 font-semibold text-app">
                    {section.label}
                  </td>
                </tr>
                {section.items.map((row) => (
                  <tr key={`${section.key}:${row.label}`} className="border-t border-border">
                    <td className="px-4 py-2 text-app">{row.label}</td>
                    <td className="px-4 py-2 text-right text-app">{formatCurrency(row.marketValue)}</td>
                    <td className="px-4 py-2 text-right text-app">{formatPercent(row.percentOfTotal, 1)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-semibold">
                  <td className="px-4 py-2 text-app">Total {section.label}</td>
                  <td className="px-4 py-2 text-right text-app">{formatCurrency(section.totalMarketValue)}</td>
                  <td className="px-4 py-2 text-right text-app">{formatPercent(section.percentOfTotal, 1)}</td>
                </tr>
              </React.Fragment>
            ))}
            <tr className="border-t-2 border-border font-semibold">
              <td className="px-4 py-3 text-app">Portfolio Total</td>
              <td className="px-4 py-3 text-right text-app">{formatCurrency(summary?.portfolioMarketValue)}</td>
              <td className="px-4 py-3 text-right text-app">100.0%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AllocationReport({ allocationByType, creditRatingSummary }) {
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <AllocationPanel title="Allocation by Investment Type" rows={allocationByType} />
      <CreditRatingSummary summary={creditRatingSummary} />
    </div>
  );
}
